/**
 * Create the document listing component.
 * 
 * @param {object} options 
 * 
 * @returns {object}
 */
const CreateDocumentListing = (options) => {
    const itemContainer = options.itemContainer instanceof HTMLElement ? options.itemContainer : null;
    const listing = options.listing instanceof HTMLElement ? options.listing : null;
    const loadMore = options.loadMore instanceof HTMLElement ? options.loadMore : null;
    const search = options.search instanceof HTMLElement ? options.search : null;
    const spinner = options.spinner instanceof HTMLElement ? options.spinner : null;
    const noResults = options.noResults instanceof HTMLElement ? options.noResults : null;
    const form = options.form instanceof HTMLElement ? options.form : null;

    // Tracking variable for documents that have already loaded into the DOM
    let totalExistingDocumentCount = 0;

    // Tracking variable for TOTAL DOCUMENTS in the selected root folder.
    let totalDocumentCount = 0;
    
    // Tracking variable for the latest loaded document.
    let documentToScroll = null;

    // This sets the minimum number of documents required before
    // showing the document search.
    const searchThreshold = 9;

    // Save the default config
    let request = {
        url: '',
        root: null,
        startPosition: 0
    };

    /**
     * Build out the description if present.
     * 
     * @param {string} description 
     * 
     * @returns {void}
     */
    const __getDescription = (description) => {
        let html = '';

        if (description.length > 0) {
            html = `<span class="smp-document__description">${description}</span>`;
        }

        return html;
    };

    /**
     * Contruct the listing using the fetched data.
     * 
     * @param {object}  data
     * 
     * @returns {void}
     */
    const __construct = (data) => {
        data.forEach((file, i) => {
            var item = document.createElement('li');

            // Set class name on list item
            item.className = 'smp-document__item';

            // Set the data-title attribute for the search function.
            item.setAttribute('data-title', file.name.toLowerCase());

            // Set the contents of the list item
            item.innerHTML = `
                <a href="${file.url}">
                    <div class="smp-document__info">
                        <div>
                            <span class="smp-document__title">${file.name}</span>
                        </div>
                        ${__getDescription(file.description)}
                    </div>
                    <div class="smp-document__cta">
                        <span class="smp-document__size">${file.size}</span>
                    </div>
                </a>
            `;

            itemContainer.appendChild(item);

            if (i === 0) {
                documentToScroll = listing.querySelector(`[data-title="${file.name.toLowerCase()}"]`);
            }
        });
    }

    /**
     * Show or hide the spinner graphic.
     * 
     * @param {boolean} toggle
     * 
     * @returns {void}
     */
    const __toggleSpinner = (toggle) => {
        switch (toggle) {
            case true:
                spinner.style.display = 'flex';
                break;
            case false:
                spinner.style.display = 'none';
                break;
            default:
                break;
        }
    };

    /**
     * Scroll down to show latest document.
     * 
     * @returns {void}
     */
    const __scrollToLatest = () => {
        itemContainer.scrollTop = documentToScroll.previousElementSibling.offsetTop;
    };

    /**
     * Fetch the data.
     * 
     * @returns (void)
     */
    const __fetch = () => {
        // Construct and run the fetch query
        const query = `${request.url}?root=${request.root}&startPosition=${request.startPosition}`;
        const fetchResponse = fetch(query);

        // Enable the loading spinner
        __toggleSpinner(true);

        fetchResponse.then(
            response => response.json()
        ).then(response => {
            // If we have response data, construct the new document elements.
            // Set the total document count tracking variable (total documents
            // in the asset listing).
            if (response.data && response.data.length > 0) {
                __construct(response.data);
                totalDocumentCount = response.totalDocumentCount ? (parseInt(response.totalDocumentCount) - 1) : 0;
            }

            // Track the number of existing documents that have been loaded into the DOM.
            totalExistingDocumentCount = Array.from(
                listing.querySelectorAll(options.itemSelector)
            ).length;

            // If the total number of loaded documents matches the total number of documents
            // from the asset listing, remove the load more button.
            if ((totalDocumentCount + 1) === totalExistingDocumentCount) {
                loadMore.classList.remove(options.active);
            }

            // Turn off the loading spinner. The active class is attached when the total number
            // of documents exceeds a certain threshold.
            __toggleSpinner(false);
            __attachActiveClass();
            __scrollToLatest();
        });
    }

    /**
     * Toggle the no results for search if nothing is surfaced.
     * 
     * @param {number} count
     * @param {string} query
     * 
     * @returns {void}
     */
    const __toggleNoResults = (count, query) => {
        const span = noResults.querySelector('span');

        if (count === 0) {
            span.innerText = `${query}`;
            noResults.style.display = 'block';
        } else {
            noResults.style.display = 'none';
        }
    };

    /**
     * Search through the document listing.
     * 
     * @returns {void}
     */
    const __searchDocuments = () => {
        const query = search.value.toLowerCase();
        const items = Array.from(
            listing.querySelectorAll(options.itemSelector)
        );
        let resultsCount = 0;

        if (items.length > 0) {
            items.forEach(item => {
                if (item.dataset.title.indexOf(query) > -1) {
                    item.style.display = 'block';
                    resultsCount += 1;
                } else {
                    item.style.display = 'none';
                }
            });

            __toggleNoResults(resultsCount, search.value);
        }
    };
    
    /**
     * Bind the necessary event listeners.
     * 
     * @returns {void}
     */
    const __bind = () => {
        // Run the fetch script
        loadMore.addEventListener('click', () => {
            // We need to do this to ensure that all documents are surfaced
            // for rendering.
            request.startPosition = request.startPosition === 0 ?
                10 :
                request.startPosition + 9
            ;

            __fetch();
        });

        // Focus on the last loaded document after running the
        // fetch (keyboard accessibility)
        loadMore.addEventListener('keyup', e => {
            const loadedDocs = Array.from(
                listing.querySelectorAll(`${options.itemSelector} a`)
            );
            let lastLoadedDoc = null;

            if (e.key === 'Enter') {
                lastLoadedDoc = loadedDocs[loadedDocs.length - 1];

                lastLoadedDoc.focus();
            }
        });

        // Search through the documents
        search.addEventListener('keyup', () => {
            __searchDocuments();
        })
    }

    /**
     * Accepts an array of HTMLElements. Ensure that all required elements exist.
     * 
     * @param {array} items 
     * 
     * @returns {void}
     */
    const __exists = (items) => {
        let valid = true;

        if (items.length > 0) {
            items.forEach(item => {
                // Operator precedence bug: `!item instanceof HTMLElement` is parsed as
                // `(!item) instanceof HTMLElement`, which is always false.
                // We need `!(item instanceof HTMLElement)` so null/non-elements are rejected.
                if (!(item instanceof HTMLElement)) {
                    valid = false;
                }
            });
        } else {
            valid = false;
        }

        return valid;
    };

    /**
     * If the total number of documents to surface is greater than the threshold,
     * show the search input field and show the load more button.
     * 
     * @returns {void}
     */
    const __attachActiveClass = () => {
        const elementsToDisplay = [form, loadMore];

        if (totalDocumentCount >= searchThreshold) {
            elementsToDisplay.forEach(el => {
                if (!el.classList.contains(options.active) &&
                    totalExistingDocumentCount < totalDocumentCount
                ) {
                    el.classList.add(options.active);
                }
            });
        }
    };

    /**
     * Initialise the component with a publicly exposed init function.
     * 
     * @returns {void}
     */
    const init = () => {
        const requiredElements = [
            form,
            itemContainer,
            listing,
            loadMore,
            noResults,
            search,
            spinner
        ];

        if (__exists(requiredElements)) {
            request.url = listing.getAttribute('data-url');
            request.root = listing.getAttribute('data-root');

            __fetch();
            __bind();
        }
    };

    /**
     * Initialise the component.
     */
    return Object.freeze({
        init
    });
};

window.addEventListener('load', () => {
    const DocumentListings = Array.from(
        document.querySelectorAll('.smp-document')
    );

    // Loop though all instances of the document listing component
    // and initialise for each one individually so we don't cross polinate
    // data between listings.
    if (DocumentListings.length > 0) {
        DocumentListings.forEach((listing) => {
            // Optional static mode:
            // When `data-static="true"` is set on the `.smp-document` root, we don't
            // fetch remote data (used for local/preview rendering).
            if (listing.dataset && listing.dataset.static === 'true') {
                return;
            }

            const DocumentListing = CreateDocumentListing({
                active: 'active',
                listing: listing,
                form: listing.querySelector('.smp-document__form'),
                itemContainer: listing.querySelector('.smp-document__listing'),
                loadMore: listing.querySelector('.smp-document__load-more'),
                noResults: listing.querySelector('.smp-document__item-no-results'),
                search: listing.querySelector('.smp-document__form input[type="text"]'),
                spinner: listing.querySelector('.smp-document__spinner'),
                itemSelector: '.smp-document__item',
            });

            DocumentListing.init();
        });
    }
});